import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Truck, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1501700493788-fa1a4fc9fe62?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function LoginPage() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0E1E38] lg:block">
        <img src={HERO} alt="Removalist truck on the road" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1E38] via-[#0E1E38]/60 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-12 text-white">
          <div className="mb-4 h-1 w-16 bg-[#FACC15]" />
          <h2 className="font-display text-4xl font-bold leading-tight">Every move,<br />booked and on time.</h2>
          <p className="mt-3 max-w-md text-sky-100/80">Internal booking and job management for the City Removalists and Storage team.</p>
        </div>
      </div>
      <div className="flex items-center justify-center bg-[#F4F7FB] p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 flex items-center gap-3">
            <div className="grid h-11 w-11 place-items-center rounded-lg bg-[#0E1E38] text-[#FACC15]"><Truck className="h-6 w-6" /></div>
            <div>
              <div className="font-display text-xl font-extrabold tracking-tight text-[#0E1E38]">CITY REMOVALISTS</div>
              <div className="text-[11px] font-medium tracking-[0.22em] text-slate-500">&amp; STORAGE · STAFF</div>
            </div>
          </div>
          <h1 className="font-display text-2xl font-bold text-[#0E1E38]">Staff sign in</h1>
          <p className="mb-6 mt-1 text-sm text-slate-500">Accounts are created by your administrator.</p>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Username</Label>
              <Input id="email" type="text" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11 bg-white" data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="h-11 bg-white" data-testid="login-password-input" />
            </div>
            {error && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-11 w-full bg-[#0E1E38] text-base hover:bg-[#162A4D]" data-testid="login-submit-button">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
